/**
 * User Profile Service
 * 处理用户公开资料相关的查询
 */

import { supabase } from './supabase';

export interface UserProfile {
  id: string;
  display_name: string;
  avatar_url?: string;
  bio?: string;
  email?: string;
  created_at: string;
  updated_at: string;
}

export interface UserPost {
  id: string;
  author_id: string;
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  content_json: any;
  view_count: number;
  like_count: number;
  comment_count: number;
  status: 'draft' | 'published';
  created_at: string;
  updated_at: string;
}

export interface UserCard {
  id: string;
  creator_id: string;
  card_type: string;
  title: string;
  description?: string;
  cover_image?: string;
  address?: string;
  rating?: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface UserTrip {
  id: string;
  creator_id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  visibility: string;
  created_at: string;
  updated_at: string;
}

/**
 * 获取用户个人资料
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('获取用户资料失败:', error);
    return null;
  }

  return data as UserProfile;
}

/**
 * 获取用户发布的游记
 */
export async function getUserPublishedPosts(
  userId: string,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: 'created_at' | 'view_count' | 'like_count';
    sortOrder?: 'asc' | 'desc';
  }
): Promise<{ data: UserPost[]; count: number }> {
  let query = supabase
    .from('travel_posts')
    .select('*', { count: 'exact' })
    .eq('author_id', userId)
    .eq('status', 'published');

  // 排序
  const sortBy = options?.sortBy || 'created_at';
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 10;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取用户游记失败:', error);
    throw error;
  }

  return { data: (data || []) as UserPost[], count: count || 0 };
}

/**
 * 获取用户发布的卡片
 */
export async function getUserPublishedCards(
  userId: string,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: 'created_at' | 'rating';
    sortOrder?: 'asc' | 'desc';
    searchQuery?: string;
    cardType?: string[];
    cities?: string[];
  }
): Promise<{ data: UserCard[]; count: number }> {
  let query = supabase
    .from('cards_cards')
    .select('*', { count: 'exact' })
    .eq('creator_id', userId)
    .eq('status', 'active');

  // 搜索
  if (options?.searchQuery) {
    const q = options.searchQuery;
    query = query.or(`title.ilike.%${q}%,description.ilike.%${q}%,address.ilike.%${q}%`);
  }

  // 类型过滤
  if (options?.cardType && options.cardType.length > 0) {
    query = query.in('card_type', options.cardType);
  }

  // 城市过滤
  if (options?.cities && options.cities.length > 0) {
    const cityConditions = options.cities.map((city: string) => `location->>city.eq.${city}`).join(',');
    query = query.or(cityConditions);
  }

  // 排序
  const sortBy = options?.sortBy || 'created_at';
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 10;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取用户卡片失败:', error);
    throw error;
  }

  return { data: (data || []) as UserCard[], count: count || 0 };
}

/**
 * 获取用户的公开行程
 */
export async function getUserPublicTrips(
  userId: string,
  options?: {
    page?: number;
    pageSize?: number;
    sortBy?: 'created_at' | 'start_date';
    sortOrder?: 'asc' | 'desc';
  }
): Promise<{ data: UserTrip[]; count: number }> {
  let query = supabase
    .from('groups')
    .select('*', { count: 'exact' })
    .eq('creator_id', userId)
    .eq('visibility', 'public');

  // 排序
  const sortBy = options?.sortBy || 'created_at';
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 10;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取用户行程失败:', error);
    throw error;
  }

  return { data: (data || []) as UserTrip[], count: count || 0 };
}

/**
 * 获取用户的粉丝数量
 */
export async function getUserFansCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('friends')
    .select('*', { count: 'exact', head: true })
    .eq('friend_id', userId)
    .eq('status', 'active');

  if (error) {
    console.error('获取粉丝数量失败:', error);
    return 0;
  }

  return count || 0;
}

/**
 * 获取用户的关注数量
 */
export async function getUserFollowingCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('friends')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'active');

  if (error) {
    console.error('获取关注数量失败:', error);
    return 0;
  }

  return count || 0;
}

/**
 * 检查是否已关注
 */
export async function checkIsFollowing(userId: string, friendId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('friends')
    .select('id')
    .eq('user_id', userId)
    .eq('friend_id', friendId)
    .eq('status', 'active')
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('检查关注状态失败:', error);
  }

  return !!data;
}

/**
 * 关注用户
 */
export async function followUser(userId: string, friendId: string): Promise<void> {
  // Check if relationship exists
  const { data: existing } = await supabase
    .from('friends')
    .select('id')
    .eq('user_id', userId)
    .eq('friend_id', friendId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('friends')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('id', existing.id);
      
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('friends')
      .insert({
        user_id: userId,
        friend_id: friendId,
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      
    if (error) throw error;
  }
}

/**
 * 取消关注
 */
export async function unfollowUser(userId: string, friendId: string): Promise<void> {
  const { error } = await supabase
    .from('friends')
    .update({ status: 'inactive', updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('friend_id', friendId);

  if (error) throw error;
}

/**
 * 获取用户的获赞数量
 */
export async function getUserLikeCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('user_likes')
    .select('*', { count: 'exact', head: true })
    .eq('liked_user_id', userId);

  if (error) {
    // If table doesn't exist, return 0 (or handle error appropriately)
    if (error.code === '42P01') return 0;
    console.error('获取获赞数量失败:', error);
    return 0;
  }

  return count || 0;
}

/**
 * 检查是否已点赞用户
 */
export async function checkIsUserLiked(userId: string, likedUserId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_likes')
    .select('id')
    .eq('user_id', userId)
    .eq('liked_user_id', likedUserId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116' && error.code !== '42P01') {
    console.error('检查点赞状态失败:', error);
  }

  return !!data;
}

/**
 * 点赞用户
 */
export async function likeUser(userId: string, likedUserId: string): Promise<void> {
  const { error } = await supabase
    .from('user_likes')
    .insert({
      user_id: userId,
      liked_user_id: likedUserId,
      created_at: new Date().toISOString()
    });

  if (error) throw error;
}

/**
 * 取消点赞用户
 */
export async function unlikeUser(userId: string, likedUserId: string): Promise<void> {
  const { error } = await supabase
    .from('user_likes')
    .delete()
    .eq('user_id', userId)
    .eq('liked_user_id', likedUserId);

  if (error) throw error;
}

/**
 * 获取用户的完整公开资料（包含游记、卡片、行程的计数）
 */
export async function getUserPublicProfile(userId: string) {
  try {
    // 并行获取所有数据
    const [profileResult, postsResult, cardsResult, tripsResult, fansCount, followingCount, likeCount] = await Promise.all([
      getUserProfile(userId),
      getUserPublishedPosts(userId, { pageSize: 1 }),
      getUserPublishedCards(userId, { pageSize: 1 }),
      getUserPublicTrips(userId, { pageSize: 1 }),
      getUserFansCount(userId),
      getUserFollowingCount(userId),
      getUserLikeCount(userId),
    ]);

    return {
      profile: profileResult,
      postsCount: postsResult.count,
      cardsCount: cardsResult.count,
      tripsCount: tripsResult.count,
      fansCount,
      followingCount,
      likeCount,
    };
  } catch (error) {
    console.error('获取用户公开资料失败:', error);
    throw error;
  }
}

/**
 * 获取用户发布卡片的可用城市列表
 */
export async function getUserPublishedCardCities(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('cards_cards')
    .select('location')
    .eq('creator_id', userId)
    .eq('status', 'active');

  if (error) {
    console.error('获取用户卡片城市失败:', error);
    return [];
  }

  const citiesSet = new Set<string>();
  data.forEach((card: any) => {
    if (card.location && typeof card.location === 'object' && card.location.city) {
      const city = card.location.city;
      if (typeof city === 'string' && city.trim()) {
        citiesSet.add(city.trim());
      }
    }
  });

  return Array.from(citiesSet).sort();
}

export default {
  getUserProfile,
  getUserPublishedPosts,
  getUserPublishedCards,
  getUserPublishedCardCities,
  getUserPublicTrips,
  getUserPublicProfile,
  getUserLikeCount,
  checkIsUserLiked,
  likeUser,
  unlikeUser,
};
